/**
 * Karplus-Strong string synthesis for the reference tones.
 *
 * A sine wave is a poor reference for tuning by ear: it has no overtones, so
 * there is nothing to beat against the string's own harmonics. A plucked
 * string does, which is exactly what this produces — a noise burst circulating
 * through a delay line that is one period long, losing its high frequencies a
 * little faster than its low ones on every pass.
 *
 * The whole renderer is a pure function over a Float32Array: no Web Audio, no
 * randomness beyond a seeded generator, so the pitch it produces can be
 * measured in a unit test rather than trusted.
 *
 * ## Getting the pitch right
 *
 * Plain Karplus-Strong rounds the delay line to whole samples, and that alone
 * is worth more than a cent: at 48 kHz the low E wants 582.4 samples, and the
 * 0.4 that get rounded away are 1.2 cents. The loop here is therefore split
 * into three delays that add up to exactly one period:
 *
 * - the delay line, a whole number of samples;
 * - the two-point averager, which is linear phase and contributes exactly half
 *   a sample at every frequency;
 * - a first-order allpass, tuned to the remaining fraction at the fundamental.
 */

import { clamp } from '../lib/math'

/** Options for one rendered pluck. */
export interface PluckOptions {
  /** Fundamental frequency in Hz. */
  readonly frequency: number
  /** Sample rate of the rendered buffer in Hz. */
  readonly sampleRate: number
  /** Time for the fundamental to fall by 60 dB, in seconds. */
  readonly decaySeconds?: number
  /** Total rendered length in seconds. */
  readonly durationSeconds?: number
  /** Seed of the excitation noise; the same seed renders the same samples. */
  readonly seed?: number
  /** Peak amplitude of the result, in (0, 1]. */
  readonly peak?: number
}

/** Time for the tone to fall by 60 dB. */
export const DEFAULT_DECAY_SECONDS = 2

/**
 * Rendered length.
 *
 * A little longer than the decay so the tail is already inaudible when the
 * release fade takes over, and the fade cannot be heard as a cut-off.
 */
export const DEFAULT_DURATION_SECONDS = 2.2

/** Fade-in of the rendered buffer, in seconds. */
export const ATTACK_SECONDS = 0.005

/** Fade-out at the end of the rendered buffer, in seconds. */
export const RELEASE_SECONDS = 0.15

/** Peak amplitude of a rendered pluck, leaving headroom below full scale. */
export const DEFAULT_PEAK = 0.8

/** Seed used when the caller does not pick one. */
export const DEFAULT_SEED = 0x5eed1e55

/** Decibels of decay that {@link PluckOptions.decaySeconds} refers to. */
const DECAY_DB = 60

/** Phase delay of the two-point averager `(1 + z^-1) / 2`, in samples. */
export const AVERAGER_DELAY_SAMPLES = 0.5

/**
 * Smallest fractional delay handed to the allpass.
 *
 * At zero its pole and zero collide on the unit circle, which is marginally
 * stable; keeping a little distance costs nothing because a whole sample can
 * always be borrowed from the delay line instead.
 */
export const MIN_FRACTIONAL_DELAY = 0.1

/**
 * One-pole smoothing applied to the excitation noise.
 *
 * White noise gives the attack a bright spark that sounds like a click rather
 * than a finger. Rolling it off leaves the same broadband excitation with a
 * softer edge.
 */
const EXCITATION_SMOOTHING = 0.45

const TWO_PI = Math.PI * 2

function assertPositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a finite positive number, got ${String(value)}`)
  }
}

/**
 * Deterministic uniform noise in [0, 1) — mulberry32.
 *
 * `Math.random` would make every rendered buffer different, and with it every
 * assertion about the result. One seed, one waveform.
 */
export function noiseSource(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Coefficient of a first-order allpass with an exact phase delay.
 *
 * For `H(z) = (a + z^-1) / (1 + a z^-1)` the phase delay at `omega` is `tau`
 * when `tan(omega * tau / 2) = k * tan(omega / 2)` and `a = (1 - k) / (1 + k)`.
 * Solving it at the fundamental rather than using the usual low-frequency
 * approximation `a = (1 - tau) / (1 + tau)` is what keeps the error in the
 * rendered pitch far below a cent.
 *
 * @param delaySamples - Wanted phase delay in samples, in (0, 2).
 * @param omega - Frequency the delay is exact at, in radians per sample.
 */
export function allpassCoefficient(delaySamples: number, omega: number): number {
  assertPositive(delaySamples, 'delaySamples')
  assertPositive(omega, 'omega')
  const k = Math.tan((omega * delaySamples) / 2) / Math.tan(omega / 2)
  return (1 - k) / (1 + k)
}

/**
 * Attenuation per loop pass for a wanted decay time.
 *
 * A sample travels the loop once per period, so `frequency * decaySeconds`
 * passes have to add up to 60 dB. Deriving the gain from the frequency keeps
 * the decay time the same on every string — a fixed gain would let the low E
 * ring several times longer than the high one.
 */
export function loopGainFor(frequency: number, decaySeconds: number): number {
  assertPositive(frequency, 'frequency')
  if (decaySeconds <= 0) return 0
  return Math.pow(10, -DECAY_DB / 20 / (decaySeconds * frequency))
}

/** How the loop is split between whole samples and the allpass. */
export interface LoopLayout {
  /** Length of the delay line in whole samples. */
  readonly lineLength: number
  /** Phase delay asked of the allpass, in samples. */
  readonly fractionalDelay: number
}

/**
 * Split one period into a whole-sample delay line and a fractional delay.
 *
 * @param frequency - Fundamental in Hz.
 * @param sampleRate - Sample rate in Hz.
 */
export function loopLayoutFor(frequency: number, sampleRate: number): LoopLayout {
  assertPositive(frequency, 'frequency')
  assertPositive(sampleRate, 'sampleRate')

  const period = sampleRate / frequency
  const remaining = period - AVERAGER_DELAY_SAMPLES - MIN_FRACTIONAL_DELAY
  const lineLength = Math.floor(remaining)
  if (lineLength < 2) {
    throw new RangeError(
      `frequency ${String(frequency)} Hz is too high for a ${String(sampleRate)} Hz string model`,
    )
  }
  return { lineLength, fractionalDelay: period - AVERAGER_DELAY_SAMPLES - lineLength }
}

/** Fill a delay line with the plucking excitation, free of any DC offset. */
function excite(line: Float32Array, seed: number): void {
  const random = noiseSource(seed)
  let smoothed = 0
  let sum = 0
  for (let i = 0; i < line.length; i += 1) {
    smoothed = EXCITATION_SMOOTHING * smoothed + (1 - EXCITATION_SMOOTHING) * (random() * 2 - 1)
    line[i] = smoothed
    sum += smoothed
  }

  // The averager in the loop passes DC untouched, so any offset left in the
  // excitation would survive as long as the note itself and show up as a
  // thump. Centring the burst removes it at the source.
  const mean = sum / line.length
  for (let i = 0; i < line.length; i += 1) {
    line[i]! -= mean
  }
}

/** Raised-cosine fades at both ends, so neither edge of the buffer clicks. */
function applyEnvelope(samples: Float32Array, attack: number, release: number): void {
  const { length } = samples
  const attackEnd = Math.min(attack, length)
  for (let i = 0; i < attackEnd; i += 1) {
    samples[i]! *= 0.5 - 0.5 * Math.cos((Math.PI * i) / attackEnd)
  }

  const releaseStart = Math.max(0, length - release)
  const releaseLength = length - releaseStart
  for (let i = releaseStart; i < length; i += 1) {
    samples[i]! *= 0.5 + 0.5 * Math.cos((Math.PI * (i - releaseStart)) / releaseLength)
  }
}

/** Scale a buffer so its loudest sample sits exactly at `peak`. */
function normalise(samples: Float32Array, peak: number): void {
  let loudest = 0
  for (const sample of samples) {
    const magnitude = Math.abs(sample)
    if (magnitude > loudest) loudest = magnitude
  }
  if (loudest === 0) return
  const factor = peak / loudest
  for (let i = 0; i < samples.length; i += 1) {
    samples[i]! *= factor
  }
}

/**
 * Render one plucked note.
 *
 * The buffer type is pinned to a plain `ArrayBuffer` because that is what
 * `AudioBuffer.copyToChannel` accepts; a shared buffer would not be.
 *
 * @returns Mono samples, peaking at {@link PluckOptions.peak} and silent at
 * both ends.
 */
export function renderPluck(options: PluckOptions): Float32Array<ArrayBuffer> {
  const { frequency, sampleRate } = options
  const decaySeconds = options.decaySeconds ?? DEFAULT_DECAY_SECONDS
  const durationSeconds = options.durationSeconds ?? DEFAULT_DURATION_SECONDS
  const peak = clamp(options.peak ?? DEFAULT_PEAK, 0, 1)

  assertPositive(durationSeconds, 'durationSeconds')
  const { lineLength, fractionalDelay } = loopLayoutFor(frequency, sampleRate)

  const omega = (TWO_PI * frequency) / sampleRate
  const coefficient = allpassCoefficient(fractionalDelay, omega)
  const gain = loopGainFor(frequency, decaySeconds)

  const line = new Float32Array(lineLength)
  excite(line, options.seed ?? DEFAULT_SEED)

  const output = new Float32Array(Math.max(1, Math.round(durationSeconds * sampleRate)))
  let index = 0
  let averagerMemory = 0
  let allpassInput = 0
  let allpassOutput = 0

  for (let i = 0; i < output.length; i += 1) {
    const delayed = line[index]!

    // Damping: the two-point average takes the highs down faster than the
    // lows, which is the whole timbre of the thing.
    const damped = (delayed + averagerMemory) * 0.5
    averagerMemory = delayed

    const shifted = coefficient * damped + allpassInput - coefficient * allpassOutput
    allpassInput = damped
    allpassOutput = shifted

    const next = gain * shifted
    line[index] = next
    index = index + 1 === lineLength ? 0 : index + 1
    output[i] = next
  }

  applyEnvelope(
    output,
    Math.round(ATTACK_SECONDS * sampleRate),
    Math.round(RELEASE_SECONDS * sampleRate),
  )
  normalise(output, peak)
  return output
}
